import APP_CONFIG from '../config/appConfig';

const API_BASE_URL = APP_CONFIG.getApiUrl();

const getAuthToken = () => {
  return localStorage.getItem('token');
};

const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const token = getAuthToken();
  
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  };

  const config = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      if (response.status === 401 && !endpoint.includes('/auth/login')) {
        if (!window.location.pathname.includes('/admin/login')) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/admin/login';
        }
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return { data };
  } catch (error) {
    throw error;
  }
};

export const authAPI = {
  register: async (email, password) => {
    return await apiRequest('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },

  login: async (email, password) => {
    return await apiRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },

  getCurrentUser: async () => {
    return await apiRequest('/auth/me');
  },
};

export const blogsAPI = {
  getAll: async () => {
    const response = await apiRequest('/blogs');
    return response;
  },

  getById: async (id) => {
    return await apiRequest(`/blogs/${id}`);
  },

  // Create blog (admin)
  create: async (blogData) => {
    const response = await apiRequest('/admin/blogs', {
      method: 'POST',
      body: JSON.stringify(blogData),
    });
    return response;
  },

  update: async (id, blogData) => {
    const response = await apiRequest(`/admin/blogs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(blogData),
    });
    return response;
  },

  delete: async (id) => {
    const response = await apiRequest(`/admin/blogs/${id}`, {
      method: 'DELETE',
    });
    return response;
  },
};

export const poemsAPI = {
  getAll: async () => {
    const response = await apiRequest('/poems');
    return response;
  },

  getFeatured: async () => {
    const response = await apiRequest('/poems/featured');
    return response;
  },

  getById: async (id) => {
    const response = await apiRequest(`/poems/${id}`);
    return response;
  },

  create: async (poemData) => {
    const response = await apiRequest('/admin/poems', {
      method: 'POST',
      body: JSON.stringify(poemData),
    });
    return response;
  },

  update: async (id, poemData) => {
    const response = await apiRequest(`/admin/poems/${id}`, {
      method: 'PUT',
      body: JSON.stringify(poemData),
    });
    return response;
  },

  delete: async (id) => {
    const response = await apiRequest(`/admin/poems/${id}`, {
      method: 'DELETE',
    });
    return response;
  },
};

export const productsAPI = {
  getAll: async () => {
    const response = await apiRequest('/products');
    return response;
  },

  getFeatured: async () => {
    const response = await apiRequest('/products/featured');
    return response;
  },

  getById: async (id) => {
    const response = await apiRequest(`/products/${id}`);
    return response;
  },

  create: async (productData) => {
    const response = await apiRequest('/admin/products', {
      method: 'POST',
      body: JSON.stringify(productData),
    });
    return response;
  },

  update: async (id, productData) => {
    const response = await apiRequest(`/admin/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(productData),
    });
    return response;
  },

  delete: async (id) => {
    const response = await apiRequest(`/admin/products/${id}`, {
      method: 'DELETE',
    });
    return response;
  },
};

export const galleryAPI = {
  getAll: async () => {
    const response = await apiRequest('/gallery');
    return response;
  },

  getFeatured: async () => {
    const response = await apiRequest('/gallery/featured');
    return response;
  },

  getById: async (id) => {
    const response = await apiRequest(`/gallery/${id}`);
    return response;
  },

  create: async (galleryData) => {
    const response = await apiRequest('/admin/gallery', {
      method: 'POST',
      body: JSON.stringify(galleryData),
    });
    return response;
  },

  update: async (id, galleryData) => {
    const response = await apiRequest(`/admin/gallery/${id}`, {
      method: 'PUT',
      body: JSON.stringify(galleryData),
    });
    return response;
  },

  delete: async (id) => {
    const response = await apiRequest(`/admin/gallery/${id}`, {
      method: 'DELETE',
    });
    return response;
  },
};

export const uploadFile = async (file, folder = 'images') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('folder', folder);

  const token = getAuthToken();
  const url = `${API_BASE_URL}/upload`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const result = await response.json();
  return result;
};

export const uploadGalleryFile = async (file, folder = 'gallery') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('folder', folder);

  const token = getAuthToken();
  const url = `${API_BASE_URL}/upload/gallery-test`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const result = await response.json();
  return result;
};

export const ordersAPI = {
  getAll: async () => {
    const response = await apiRequest('/orders');
    return response;
  },

  getById: async (id) => {
    const response = await apiRequest(`/orders/${id}`);
    return response;
  },

  getByOrderNumber: async (orderNumber) => {
    const response = await apiRequest(`/orders/number/${orderNumber}`);
    return response;
  },

  update: async (id, orderData) => {
    const response = await apiRequest(`/orders/${id}`, {
      method: 'PUT',
      body: JSON.stringify(orderData),
    });
    return response;
  },

  resendEmail: async (id) => {
    const response = await apiRequest(`/orders/${id}/resend-email`, {
      method: 'POST',
    });
    return response;
  },

  create: async (orderData) => {
    const response = await apiRequest('/orders', {
      method: 'POST',
      body: JSON.stringify(orderData),
    });
    return response;
  },
};

export default {
  authAPI,
  blogsAPI,
  poemsAPI,
  galleryAPI,
  productsAPI,
  ordersAPI,
  uploadFile,
};

